import {
  WORKBOOK_MAX_ROWS,
  type WorkbookCell,
  type WorkbookDefinition,
} from "@erp/shared-types";

/**
 * A Parameter: an Inline table a caller (a Run Workbook node, an agent) may
 * replace for one Run, like a notebook's parameters cell. Every other Source
 * is read as the Workbook reads it.
 */
export type WorkbookParameter = {
  sourceId: string;
  name: string;
  columns: readonly string[];
};

/** Rows keyed by column name; one object is a one-row table. */
export type WorkbookParameterRows =
  Record<string, unknown> | readonly Record<string, unknown>[];

/** Parameter values keyed by the Inline table's name, matched ignoring case. */
export type WorkbookParameterValues = Record<string, WorkbookParameterRows>;

/** A Parameter value that cannot replace its Inline table. The message is for users. */
export class WorkbookParameterError extends Error {
  override name = "WorkbookParameterError";
}

export function workbookParameters(
  definition: Pick<WorkbookDefinition, "sources">,
): WorkbookParameter[] {
  return definition.sources.flatMap((source) =>
    source.kind === "input"
      ? [
          {
            sourceId: source.id,
            name: source.name,
            columns: source.table.columns,
          },
        ]
      : [],
  );
}

function listed(names: readonly string[]): string {
  return names.join(", ");
}

/** A cell as a Source holds it: text, a number or TRUE/FALSE as text, or blank. */
function parameterCell(value: unknown, describe: () => string): WorkbookCell {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  throw new WorkbookParameterError(
    `${describe()} must be text, a number or TRUE/FALSE.`,
  );
}

function parameterTable(
  parameter: WorkbookParameter,
  value: WorkbookParameterRows,
): WorkbookCell[][] {
  const records: readonly Record<string, unknown>[] = Array.isArray(value)
    ? value
    : [value];
  if (records.length > WORKBOOK_MAX_ROWS)
    throw new WorkbookParameterError(
      `'${parameter.name}' can hold at most ${WORKBOOK_MAX_ROWS} rows.`,
    );
  const columnIndex = new Map(
    parameter.columns.map((column, index) => [column.toLowerCase(), index]),
  );
  return records.map((record: unknown, rowIndex) => {
    if (typeof record !== "object" || record === null || Array.isArray(record))
      throw new WorkbookParameterError(
        `'${parameter.name}' row ${rowIndex + 1} must be an object of column names to cells.`,
      );
    const cells: WorkbookCell[] = parameter.columns.map(() => null);
    for (const [key, cell] of Object.entries(record)) {
      const index = columnIndex.get(key.toLowerCase());
      if (index === undefined)
        throw new WorkbookParameterError(
          `'${parameter.name}' has no column '${key}'. Its columns are ${listed(parameter.columns)}.`,
        );
      cells[index] = parameterCell(
        cell,
        () =>
          `'${parameter.name}' row ${rowIndex + 1}, column '${parameter.columns[index]}'`,
      );
    }
    return cells;
  });
}

/**
 * The Definition a parameterized Run executes: each named Inline table's rows
 * replaced by the given ones. The Definition passed in is not changed. Because
 * the Snapshot holds the replaced table, the Run's Results carry their own
 * Fingerprints and show as Stale against the saved Workbook.
 */
export function applyWorkbookParameters(
  definition: WorkbookDefinition,
  values: WorkbookParameterValues,
): WorkbookDefinition {
  const parameters = workbookParameters(definition);
  const byName = new Map(
    parameters.map((parameter) => [parameter.name.toLowerCase(), parameter]),
  );
  const rows = new Map<string, WorkbookCell[][]>();
  for (const [name, value] of Object.entries(values)) {
    const parameter = byName.get(name.toLowerCase());
    if (!parameter)
      throw new WorkbookParameterError(
        parameters.length
          ? `'${name}' isn't a parameter of this workbook. Its parameters are ${listed(parameters.map((item) => item.name))}.`
          : `'${name}' isn't a parameter of this workbook. It has no inline tables to replace.`,
      );
    rows.set(parameter.sourceId, parameterTable(parameter, value));
  }
  if (!rows.size) return definition;
  return {
    ...definition,
    sources: definition.sources.map((source) => {
      const replaced = rows.get(source.id);
      return source.kind === "input" && replaced
        ? {
            ...source,
            table: { columns: source.table.columns, rows: replaced },
          }
        : source;
    }),
  };
}
